import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Wifi, Navigation, Clock, Smartphone, Satellite, Battery, Thermometer, Gauge, Activity, Compass, Mountain, ChevronDown, ChevronUp, Code, Droplets, Sun, Wind, Zap, BatteryCharging } from 'lucide-react';
import { GpsData, supabase } from '../lib/supabase';
import { format } from 'date-fns';

interface GpsDetailModalProps {
  data: GpsData | null;
  isOpen: boolean;
  onClose: () => void;
}

const GpsDetailModal: React.FC<GpsDetailModalProps> = ({ data, isOpen, onClose }) => {
  const [showRawData, setShowRawData] = useState(false);
  const [cycleStats, setCycleStats] = useState<{
    eventsSinceCharge: number | null;
    daysSinceCharge: number | null;
    lastFullChargeAt: string | null;
    loading: boolean;
  }>({ eventsSinceCharge: null, daysSinceCharge: null, lastFullChargeAt: null, loading: false });
  
  // Compute cycle stats: count readings since last full charge (battery 100%),
  // and days elapsed since that charge. Resets on next 100% event.
  useEffect(() => {
    let cancelled = false;
    async function loadCycleStats() {
      // Guard: data can be null at type level even though we return early above
      if (!data || !data.device_id) {
        setCycleStats({ eventsSinceCharge: null, daysSinceCharge: null, lastFullChargeAt: null, loading: false });
        return;
      }
      const deviceId = data.device_id;
      try {
        setCycleStats(prev => ({ ...prev, loading: true }));
        // Find last time battery reached 100%
        const { data: lastChargeRows, error: lastErr } = await supabase
          .from('gps_data')
          .select('created_at')
          .eq('device_id', deviceId)
          .gte('battery_percentage', 100)
          .order('created_at', { ascending: false })
          .limit(1);
        if (lastErr) throw lastErr;
        const lastFull = (lastChargeRows && lastChargeRows[0]?.created_at) || null;
        // If never fully charged, fall back to earliest known point in the last 30 days
        const sinceIso = lastFull || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        // Count events since that time for this device
        const { count: evCount, error: countErr } = await supabase
          .from('gps_data')
          .select('id', { count: 'exact', head: true })
          .eq('device_id', deviceId)
          .gte('created_at', sinceIso);
        if (countErr) throw countErr;
        // Days since charge: elapsed days between now and sinceIso (at least 0)
        const days = Math.max(0, Math.floor((Date.now() - Date.parse(sinceIso)) / (24 * 60 * 60 * 1000)));
        if (!cancelled) {
          setCycleStats({
            eventsSinceCharge: evCount ?? 0,
            daysSinceCharge: days,
            lastFullChargeAt: lastFull,
            loading: false
          });
        }
      } catch {
        if (!cancelled) {
          setCycleStats({ eventsSinceCharge: null, daysSinceCharge: null, lastFullChargeAt: null, loading: false });
        }
      }
    }
    loadCycleStats();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.device_id, isOpen]);

  // Guard render after hooks (to keep hooks order stable)
  if (!isOpen || !data) return null;

  const formatCoordinate = (value: number, isLatitude: boolean) => {
    const absolute = Math.abs(value);
    const degrees = Math.floor(absolute);
    const minutes = (absolute - degrees) * 60;
    const direction = isLatitude 
      ? value >= 0 ? 'N' : 'S'
      : value >= 0 ? 'E' : 'W';
    return `${degrees}°${minutes.toFixed(3)}'${direction}`;
  };

  const getSourceIcon = (source: string) => {
    switch (source?.toUpperCase()) {
      case 'WIFI':
        return <Wifi className="w-5 h-5" />;
      case 'GPS':
        return <Satellite className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  const batteryColor = data.battery_percentage > 60 ? 'text-green-500' : 
                       data.battery_percentage > 30 ? 'text-yellow-500' : 'text-red-500';
  
  // Determine actual location source based on satellites
  const actualLocationSource = data.satellites > 0 ? 'GPS' : data.location_source;
  const isLocationSourceMismatch = data.location_source === 'WIFI' && data.satellites > 0;
  // Parse raw_data when it is a string and derive fields missing from RPC
  let parsedRaw: any = null;
  try {
    parsedRaw = typeof data.raw_data === 'string' 
      ? JSON.parse(data.raw_data as unknown as string) 
      : data.raw_data;
  } catch {
    parsedRaw = null;
  }
  // Derive ICCID from either column or parsed raw_data
  const iccidValue = (data as any)?.iccid || parsedRaw?.iccid;
  // Prefer top-level environmental fields; fall back to parsed raw sensors when needed
  const envTemperature = (data as any).temperature ?? parsedRaw?.sensors?.temperature ?? parsedRaw?.temperature;
  const envHumidity = (data as any).humidity ?? parsedRaw?.sensors?.humidity ?? parsedRaw?.humidity;
  const envLight = (data as any).light_level ?? parsedRaw?.sensors?.light ?? parsedRaw?.light_level;
  const envPressure = (data as any).pressure ?? parsedRaw?.pressure;
  // Determine if there is any environmental data to show
  const hasEnv =
    (envTemperature !== null && envTemperature !== undefined) ||
    (envHumidity !== null && envHumidity !== undefined) ||
    (envLight !== null && envLight !== undefined) ||
    (envPressure !== null && envPressure !== undefined);

  // Use Portal to render outside the map's DOM tree
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4 modal-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl modal-content">
        {/* Header */}
        <div className="bg-gradient-to-br from-traqpack-yellow to-yellow-400 p-6 text-black">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/10 hover:bg-black/20 rounded-full transition-all"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-black/10 rounded-2xl flex items-center justify-center">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Location Details</h2>
              <p className="text-black/70">Device {data.device_nickname || data.device_id}</p>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Device Card */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Device</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">IMEI</p>
                  <p className="font-mono text-sm text-traqpack-gray-900">{data.raw_data?.imei || data.device_id}</p>
                </div>
                
                {/* Nickname */}
                {data.device_nickname && (
                  <div>
                    <p className="text-xs text-traqpack-gray-500 mb-1">Nickname</p>
                    <p className="text-sm text-traqpack-gray-900">{data.device_nickname}</p>
                  </div>
                )}
                
                {/* Firmware Version */}
                {data.firmware_version && (
                  <div>
                    <p className="text-xs text-traqpack-gray-500 mb-1">Firmware Version</p>
                    <p className="font-mono text-xs text-traqpack-gray-900">{data.firmware_version}</p>
                  </div>
                )}
                
                {/* ICCID (SIM Card ID) */}
                {iccidValue && (
                  <div>
                    <p className="text-xs text-traqpack-gray-500 mb-1">ICCID (SIM)</p>
                    <p className="font-mono text-xs text-traqpack-gray-900">{iccidValue}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Location Source</p>
                  <div className="flex items-center space-x-2">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                      actualLocationSource === 'GPS' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {getSourceIcon(actualLocationSource)}
                    </div>
                    <span className="text-sm font-medium">
                      {actualLocationSource}
                      {isLocationSourceMismatch && (
                        <span className="text-xs text-traqpack-gray-500 ml-1">
                          (reported: {data.location_source})
                        </span>
                      )}
                    </span>
                    {/* Show info if satellites are being used */}
                    {actualLocationSource === 'GPS' && (
                      <div className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-lg">
                        {data.satellites} satellites
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Location Card */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Coordinates</h3>
              </div>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Latitude</p>
                  <p className="font-mono text-sm text-traqpack-gray-900">{formatCoordinate(data.latitude, true)}</p>
                  <p className="text-xs text-traqpack-gray-600">{data.latitude.toFixed(6)}°</p>
                </div>
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Longitude</p>
                  <p className="font-mono text-sm text-traqpack-gray-900">{formatCoordinate(data.longitude, false)}</p>
                  <p className="text-xs text-traqpack-gray-600">{data.longitude.toFixed(6)}°</p>
                </div>
              </div>
            </div>

            {/* Address Card - Full Width */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4 md:col-span-2">
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <Navigation className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Address</h3>
              </div>
              <p className="text-sm text-traqpack-gray-700 leading-relaxed">
                {data.address || `${data.latitude.toFixed(6)}, ${data.longitude.toFixed(6)}`}
              </p>
            </div>

            {/* Movement Stats */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <Gauge className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Movement</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Speed</p>
                  <p className="text-xl font-semibold text-traqpack-gray-900">{data.speed}</p>
                  <p className="text-xs text-traqpack-gray-600">km/h</p>
                </div>
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Direction</p>
                  <div className="flex items-center space-x-2">
                    <Compass className="w-5 h-5 text-traqpack-gray-600" />
                    <span className="text-xl font-semibold text-traqpack-gray-900">{data.direction}°</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Altitude</p>
                  <div className="flex items-center space-x-2">
                    <Mountain className="w-4 h-4 text-traqpack-gray-600" />
                    <span className="text-lg font-semibold text-traqpack-gray-900">{data.altitude}m</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Mileage</p>
                  <p className="text-lg font-semibold text-traqpack-gray-900">
                    {data.mileage !== null ? `${data.mileage} km` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Environmental Sensors */}
            {hasEnv && (
              <div className="bg-traqpack-gray-50 rounded-2xl p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                    <Sun className="w-4 h-4 text-traqpack-yellow" />
                  </div>
                  <h3 className="font-semibold text-traqpack-gray-900">Environmental</h3>
                </div>
                
                <div className="space-y-3">
                  {/* Temperature */}
                  {envTemperature !== null && envTemperature !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Thermometer className="w-4 h-4 text-traqpack-gray-600" />
                        <span className="text-xs text-traqpack-gray-600">Temperature</span>
                      </div>
                      <span className="text-sm font-semibold">{envTemperature}°C</span>
                    </div>
                  )}

                  {/* Humidity */}
                  {envHumidity !== null && envHumidity !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Droplets className="w-4 h-4 text-traqpack-gray-600" />
                        <span className="text-xs text-traqpack-gray-600">Humidity</span>
                      </div>
                      <span className="text-sm font-semibold">{envHumidity}%</span>
                    </div>
                  )}

                  {/* Light Level */}
                  {envLight !== null && envLight !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Sun className="w-4 h-4 text-traqpack-gray-600" />
                        <span className="text-xs text-traqpack-gray-600">Light Level</span>
                      </div>
                      <span className="text-sm font-semibold">{envLight} lux</span>
                    </div>
                  )}

                  {/* Pressure */}
                  {envPressure !== null && envPressure !== undefined && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Wind className="w-4 h-4 text-traqpack-gray-600" />
                        <span className="text-xs text-traqpack-gray-600">Pressure</span>
                      </div>
                      <span className="text-sm font-semibold">{envPressure} hPa</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Power & Connectivity */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <Activity className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Power & Connectivity</h3>
              </div>
              
              <div className="space-y-4">
                {/* Battery */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <Battery className={`w-4 h-4 ${batteryColor}`} />
                      <span className="text-xs text-traqpack-gray-600">Battery</span>
                    </div>
                    <span className="text-sm font-semibold">{data.battery_percentage}%</span>
                  </div>
                  <div className="w-full bg-traqpack-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${
                        data.battery_percentage > 60 ? 'bg-green-500' : 
                        data.battery_percentage > 30 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${data.battery_percentage}%` }}
                    />
                  </div>
                </div>

                {/* Battery Voltage */}
                {data.battery_voltage !== null && data.battery_voltage !== undefined && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-traqpack-gray-600" />
                      <span className="text-xs text-traqpack-gray-600">Voltage</span>
                    </div>
                    <span className="text-sm font-semibold">{data.battery_voltage.toFixed(2)}V</span>
                  </div>
                )}

                {/* Charging State */}
                {data.charging_state && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <BatteryCharging className="w-4 h-4 text-traqpack-gray-600" />
                      <span className="text-xs text-traqpack-gray-600">Charging</span>
                    </div>
                    <span className={`text-sm font-semibold ${
                      data.charging_state.toLowerCase() === 'charging' ? 'text-green-600' : 'text-traqpack-gray-700'
                    }`}>
                      {data.charging_state}
                    </span>
                  </div>
                )}

                {/* Signal */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <Wifi className="w-4 h-4 text-traqpack-gray-600" />
                      <span className="text-xs text-traqpack-gray-600">Signal</span>
                    </div>
                    <span className="text-sm font-semibold">{data.signal_strength}%</span>
                  </div>
                  <div className="w-full bg-traqpack-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${data.signal_strength}%` }}
                    />
                  </div>
                </div>

                {/* Satellites */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Satellite className="w-4 h-4 text-traqpack-gray-600" />
                    <span className="text-xs text-traqpack-gray-600">Satellites</span>
                  </div>
                  <span className="text-sm font-semibold">{data.satellites}</span>
                </div>
              </div>
            </div>

            {/* Cycle Activity Stats */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <Activity className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Cycle Activity Stats</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-traqpack-gray-600">Events since full charge</span>
                  <span className="text-sm font-semibold">
                    {cycleStats.loading ? '...' : (cycleStats.eventsSinceCharge ?? 'N/A')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-traqpack-gray-600">Days since full charge</span>
                  <span className="text-sm font-semibold">
                    {cycleStats.loading ? '...' : (cycleStats.daysSinceCharge ?? 'N/A')}
                  </span>
                </div>
                {cycleStats.lastFullChargeAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-traqpack-gray-600">Last full charge</span>
                    <span className="text-xs text-traqpack-gray-700">
                      {format(new Date(cycleStats.lastFullChargeAt), 'PPpp')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Timestamps Card - Full Width */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-4 h-4 text-traqpack-yellow" />
                </div>
                <h3 className="font-semibold text-traqpack-gray-900">Time Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Device Time</p>
                  <p className="text-sm font-medium text-traqpack-gray-900">
                    {(() => {
                      try {
                        return data.device_timestamp
                          ? format(new Date(data.device_timestamp), 'PPpp')
                          : 'N/A';
                      } catch {
                        return 'N/A';
                      }
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-traqpack-gray-500 mb-1">Server Time</p>
                  <p className="text-sm font-medium text-traqpack-gray-900">
                    {(() => {
                      try {
                        return data.created_at
                          ? format(new Date(data.created_at), 'PPpp')
                          : 'N/A';
                      } catch {
                        return 'N/A';
                      }
                    })()}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Raw Data Card - Full Width */}
            <div className="bg-traqpack-gray-50 rounded-2xl p-4 md:col-span-2">
              <button
                onClick={() => setShowRawData(!showRawData)}
                className="w-full flex items-center justify-between group"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center">
                    <Code className="w-4 h-4 text-traqpack-yellow" />
                  </div>
                  <h3 className="font-semibold text-traqpack-gray-900">Raw Data (Debug)</h3>
                </div>
                <div className="p-1 rounded-lg hover:bg-traqpack-gray-200/50 transition-colors">
                  {showRawData ? (
                    <ChevronUp className="w-5 h-5 text-traqpack-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-traqpack-gray-600" />
                  )}
                </div>
              </button>
              
              {showRawData && (
                <div className="mt-4 p-4 bg-traqpack-gray-900 rounded-xl overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                    {JSON.stringify(data.raw_data || data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GpsDetailModal;